/**
 * Map full team names to their league abbreviations.
 * Used for displaying team codes in Today's Picks, Weekly Selections and on
 * reveal cards ("vs SEA 8:20pm ET").
 *
 * NOTE(DKPLAT-364): Prefer trusting `card.teamAbbreviation` from the backend
 * when it is available. These maps exist because day fixtures only carry team
 * *names* (`daily_reveal_day_fixtures.home_team` / `away_team`), so the opponent
 * code on a card has to be derived from the name. The three-character slice
 * fallback below is wrong for plenty of clubs — "New England Patriots" became
 * "NEW" rather than "NE" — so every club whose code is not simply its first
 * three letters must be listed here.
 */
const MLB_TEAM_NAME_TO_ABBR: Record<string, string> = {
  'arizona diamondbacks': 'ARI',
  'atlanta braves': 'ATL',
  'baltimore orioles': 'BAL',
  'boston red sox': 'BOS',
  'chicago cubs': 'CHC',
  'chicago white sox': 'CWS',
  'cincinnati reds': 'CIN',
  'cleveland guardians': 'CLE',
  'cleveland indians': 'CLE',
  'colorado rockies': 'COL',
  'detroit tigers': 'DET',
  'houston astros': 'HOU',
  'kansas city royals': 'KC',
  'los angeles angels': 'LAA',
  'los angeles dodgers': 'LAD',
  'miami marlins': 'MIA',
  'milwaukee brewers': 'MIL',
  'minnesota twins': 'MIN',
  'new york mets': 'NYM',
  'new york yankees': 'NYY',
  'oakland athletics': 'OAK',
  'philadelphia phillies': 'PHI',
  'pittsburgh pirates': 'PIT',
  'san diego padres': 'SD',
  'san francisco giants': 'SF',
  'seattle mariners': 'SEA',
  'st. louis cardinals': 'STL',
  'st louis cardinals': 'STL',
  'st. louis': 'STL',
  'st louis': 'STL',
  'tampa bay rays': 'TB',
  'texas rangers': 'TEX',
  'toronto blue jays': 'TOR',
  'washington nationals': 'WSH',
  // Common short names (quoted keys for names with spaces)
  mets: 'NYM',
  cardinals: 'STL',
  yankees: 'NYY',
  'red sox': 'BOS',
  dodgers: 'LAD',
  cubs: 'CHC',
  braves: 'ATL',
  phillies: 'PHI',
  orioles: 'BAL',
  guardians: 'CLE',
  astros: 'HOU',
  mariners: 'SEA',
  twins: 'MIN',
  tigers: 'DET',
  royals: 'KC',
  'white sox': 'CWS',
  indians: 'CLE',
  angels: 'LAA',
  rangers: 'TEX',
  athletics: 'OAK',
  'blue jays': 'TOR',
  rays: 'TB',
  reds: 'CIN',
  brewers: 'MIL',
  pirates: 'PIT',
  padres: 'SD',
  giants: 'SF',
  diamondbacks: 'ARI',
  'd-backs': 'ARI',
  'arizona d-backs': 'ARI',
  /** Feed sometimes sends city-only */
  arizona: 'ARI',
  rockies: 'COL',
  nationals: 'WSH',
  marlins: 'MIA',
};

/**
 * NFL clubs, keyed by the shapes the Sportradar feed writes into the fixture
 * name columns: full name ("New England Patriots"), market ("New England") and
 * nickname ("Patriots"). Codes are the Sportradar aliases, which are also what
 * the backend stores on a card's `team_abbreviation`, so a card and its fixture
 * resolve to the same string and the opponent can be picked out reliably.
 *
 * Markets shared with an MLB club are omitted where the two leagues disagree
 * ("washington" is WSH in MLB and WAS in the NFL); the full name always wins,
 * and the ambiguous city-only form is left to the shared entries below.
 */
const NFL_TEAM_NAME_TO_ABBR: Record<string, string> = {
  'arizona cardinals': 'ARI',
  'atlanta falcons': 'ATL',
  'baltimore ravens': 'BAL',
  'buffalo bills': 'BUF',
  'carolina panthers': 'CAR',
  'chicago bears': 'CHI',
  'cincinnati bengals': 'CIN',
  'cleveland browns': 'CLE',
  'dallas cowboys': 'DAL',
  'denver broncos': 'DEN',
  'detroit lions': 'DET',
  'green bay packers': 'GB',
  'houston texans': 'HOU',
  'indianapolis colts': 'IND',
  'jacksonville jaguars': 'JAX',
  'kansas city chiefs': 'KC',
  'las vegas raiders': 'LV',
  'los angeles chargers': 'LAC',
  'los angeles rams': 'LAR',
  'miami dolphins': 'MIA',
  'minnesota vikings': 'MIN',
  'new england patriots': 'NE',
  'new orleans saints': 'NO',
  'new york giants': 'NYG',
  'new york jets': 'NYJ',
  'philadelphia eagles': 'PHI',
  'pittsburgh steelers': 'PIT',
  'san francisco 49ers': 'SF',
  'seattle seahawks': 'SEA',
  'tampa bay buccaneers': 'TB',
  'tennessee titans': 'TEN',
  'washington commanders': 'WAS',
  // Market-only ("New England") — omitted where an MLB club shares the market
  // with a different code (Washington) or where the market is ambiguous within
  // the NFL itself (New York, Los Angeles).
  'new england': 'NE',
  'new orleans': 'NO',
  'green bay': 'GB',
  'las vegas': 'LV',
  'kansas city': 'KC',
  'tampa bay': 'TB',
  jacksonville: 'JAX',
  buffalo: 'BUF',
  carolina: 'CAR',
  denver: 'DEN',
  indianapolis: 'IND',
  tennessee: 'TEN',
  dallas: 'DAL',
  // Nickname-only, excluding nicknames an MLB club already claims here
  // ("cardinals" is St. Louis, "giants" is San Francisco).
  patriots: 'NE',
  seahawks: 'SEA',
  packers: 'GB',
  chiefs: 'KC',
  raiders: 'LV',
  chargers: 'LAC',
  rams: 'LAR',
  saints: 'NO',
  jets: 'NYJ',
  '49ers': 'SF',
  buccaneers: 'TB',
  commanders: 'WAS',
  jaguars: 'JAX',
  bengals: 'CIN',
  ravens: 'BAL',
  browns: 'CLE',
  bills: 'BUF',
  panthers: 'CAR',
  bears: 'CHI',
  cowboys: 'DAL',
  broncos: 'DEN',
  lions: 'DET',
  texans: 'HOU',
  colts: 'IND',
  dolphins: 'MIA',
  vikings: 'MIN',
  eagles: 'PHI',
  steelers: 'PIT',
  titans: 'TEN',
  falcons: 'ATL',
};

/**
 * NBA clubs. Same reasoning as the NFL map: the slice fallback turns
 * "Golden State Warriors" into "GOL" and "Los Angeles Lakers" into "LOS".
 */
const NBA_TEAM_NAME_TO_ABBR: Record<string, string> = {
  'atlanta hawks': 'ATL',
  'boston celtics': 'BOS',
  'brooklyn nets': 'BKN',
  'charlotte hornets': 'CHA',
  'chicago bulls': 'CHI',
  'cleveland cavaliers': 'CLE',
  'dallas mavericks': 'DAL',
  'denver nuggets': 'DEN',
  'detroit pistons': 'DET',
  'golden state warriors': 'GSW',
  'houston rockets': 'HOU',
  'indiana pacers': 'IND',
  'los angeles clippers': 'LAC',
  'los angeles lakers': 'LAL',
  'memphis grizzlies': 'MEM',
  'miami heat': 'MIA',
  'milwaukee bucks': 'MIL',
  'minnesota timberwolves': 'MIN',
  'new orleans pelicans': 'NOP',
  'new york knicks': 'NYK',
  'oklahoma city thunder': 'OKC',
  'orlando magic': 'ORL',
  'philadelphia 76ers': 'PHI',
  'phoenix suns': 'PHX',
  'portland trail blazers': 'POR',
  'sacramento kings': 'SAC',
  'san antonio spurs': 'SAS',
  'toronto raptors': 'TOR',
  'utah jazz': 'UTA',
  'washington wizards': 'WAS',
  // Nickname-only, excluding nicknames another league's club already claims.
  hawks: 'ATL',
  celtics: 'BOS',
  nets: 'BKN',
  hornets: 'CHA',
  bulls: 'CHI',
  cavaliers: 'CLE',
  mavericks: 'DAL',
  nuggets: 'DEN',
  pistons: 'DET',
  warriors: 'GSW',
  pacers: 'IND',
  clippers: 'LAC',
  lakers: 'LAL',
  grizzlies: 'MEM',
  heat: 'MIA',
  bucks: 'MIL',
  timberwolves: 'MIN',
  pelicans: 'NOP',
  knicks: 'NYK',
  thunder: 'OKC',
  magic: 'ORL',
  '76ers': 'PHI',
  suns: 'PHX',
  'trail blazers': 'POR',
  spurs: 'SAS',
  raptors: 'TOR',
  jazz: 'UTA',
  wizards: 'WAS',
};

/**
 * Lookup order is MLB first so the existing MLB behaviour is untouched, then
 * NFL, then NBA. Keys shared across the maps ("seattle", "cleveland") resolve
 * to the same code in every league, so the order only matters for keys that are
 * deliberately absent from the later maps.
 */
const TEAM_NAME_TO_ABBR: Record<string, string> = {
  ...NBA_TEAM_NAME_TO_ABBR,
  ...NFL_TEAM_NAME_TO_ABBR,
  ...MLB_TEAM_NAME_TO_ABBR,
};

/**
 * Placeholders the backend has written into the NOT NULL `team_name` column when a player's
 * club could not be resolved. The three-character fallback below turns any of them into a
 * convincing-looking team code — "Unknown" rendered on the card as "UNK" — so they are
 * treated as the absence of a team, which is what they mean.
 */
const UNKNOWN_TEAM_NAMES = new Set(['unknown', 'unknown team', 'n/a', 'tbd', 'none']);

/**
 * Look a club name up in the league maps, without the slice fallback.
 * Returns null for anything not listed, so callers can tell a real mapping
 * ("New England Patriots" → "NE") from a guess ("Nittany Lions" → "NIT").
 */
export function lookupTeamAbbreviation(team: string | null | undefined): string | null {
  if (!team || typeof team !== 'string') return null;
  return TEAM_NAME_TO_ABBR[team.trim().toLowerCase()] ?? null;
}

/**
 * Convert a team name or existing code to a short uppercase abbreviation.
 * - If already 2-3 chars, returns uppercase
 * - Otherwise looks up the MLB/NFL/NBA mappings
 * - Fallback: uppercase first 3 chars of name
 */
export function toTeamAbbreviation(team: string): string {
  if (!team || typeof team !== 'string') return '';
  const trimmed = team.trim();
  if (UNKNOWN_TEAM_NAMES.has(trimmed.toLowerCase())) return '';
  if (trimmed.length <= 3) return trimmed.toUpperCase();
  return lookupTeamAbbreviation(trimmed) ?? trimmed.slice(0, 3).toUpperCase();
}

/**
 * Resolve a team abbreviation from both explicit code and full team name.
 *
 * The backend's own `team_abbreviation` is the more trustworthy of the two, so
 * the name only wins when it maps to a known club code. Otherwise a club that
 * is missing from the maps above would have its correct stored code replaced by
 * the first three letters of its name.
 */
export function resolveTeamAbbreviation(
  teamAbbreviation: string | null | undefined,
  teamName: string | null | undefined
): string {
  const fromCode = toTeamAbbreviation(teamAbbreviation ?? '');
  const fromName = toTeamAbbreviation(teamName ?? '');
  const mappedName = lookupTeamAbbreviation(teamName);

  if (mappedName && fromCode && mappedName !== fromCode) return mappedName;
  return fromCode || fromName;
}
